<div class="popup-convert-proposal prosuite-modal" [formGroup]="sectionForm" role="dialog" aria-labelledby="export-pdf-title" aria-describedby="export-pdf-description">
  <!-- Enhanced Header with ProSuite Design System -->
  <div class="popup-header-enhanced" role="banner">
    <div class="header-content">
      <h4 id="export-pdf-title" class="header-title">
        <mat-icon class="header-icon">picture_as_pdf</mat-icon>
        Export To PDF
      </h4>
      <button type="button"
        class="close-button-enhanced"
        mat-dialog-close
        aria-label="Close export PDF dialog"
        role="button"
        tabindex="0">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Loading Overlay -->
  @if (isProcessing) {
    <div class="loading-overlay">
      <div class="loading-overlay-card">
        <div class="spinner"></div>
        <span class="loading-overlay-text">{{ processingText }}</span>
      </div>
    </div>
  }

  <div class="modal-body-enhanced">
    <!-- Template Selection Section -->
    <div class="template-selection-wrapper">
      <div class="template-section-header">
        <div class="template-title-group">
          <mat-icon class="template-icon">description</mat-icon>
          <h5>Select Templates</h5>
          @if (getSelectedTemplatesCount() > 0) {
            <span class="template-count-badge">
              {{ getSelectedTemplatesCount() }}
            </span>
          }
        </div>
        @if (getSelectedTemplatesCount() > 0) {
          <button type="button"
            class="clear-all-btn"
            (click)="clearAllTemplates()">
            <mat-icon>clear_all</mat-icon>
            Clear All
          </button>
        }
      </div>

      <div class="template-selection-card">
        <label for="templates" class="template-label">
          Choose templates to include in your PDF export
          <span class="required-indicator">*</span>
        </label>

        <!-- Loading State -->
        @if (isLoadingTemplates) {
          <div class="template-loading">
            <mat-spinner diameter="24"></mat-spinner>
            <span class="loading-text">Loading templates...</span>
          </div>
        }

        <!-- Template Selector -->
        @if (!isLoadingTemplates) {
          <ng-select
            id="templates"
            [items]="templates"
            placeholder="Search and select templates..."
            [closeOnSelect]="false"
            [multiple]="true"
            formControlName="templates"
            bindLabel="template_name"
            [trackByFn]="trackByTemplateId"
            [loading]="isLoadingTemplates"
            [disabled]="isProcessing"
            aria-describedby="template-help-text">
            <ng-template ng-option-tmp let-item="item" let-index="index">
              <div class="template-option">
                <mat-icon class="template-type-icon">{{ item.is_default ? 'star' : 'insert_drive_file' }}</mat-icon>
                <span class="template-name">{{ item.template_name }}</span>
                @if (item.is_default) {
                  <span class="default-badge">Default</span>
                }
              </div>
            </ng-template>
          </ng-select>
        }

        <!-- Additional files — attached files are merged into the PDF for
             Download, Preview and Save Document (shown only where the module
             supports it, see canAttachFiles). -->
        @if (canAttachFiles) {
          <div class="additional-files-header">
            <h5 class="additional-files-title">Additional files</h5>
            <mat-slide-toggle class="additional-files-toggle b-one-switch" [checked]="includeAdditionalFiles"
              (change)="onToggleAdditionalFiles($event.checked)" aria-label="Include additional files in the PDF">
            </mat-slide-toggle>
          </div>
          @if (includeAdditionalFiles) {
          @if (additionalFiles.length) {
            <ul class="additional-files-list" cdkDropList cdkDropListLockAxis="y" (cdkDropListDropped)="dropAdditionalFile($event)">
              @for (file of additionalFiles; track file.id) {
                <li class="additional-file-item" cdkDrag cdkDragPreviewContainer="parent" title="Click and move to reorder">
                  <mat-icon class="drag-hint">drag_indicator</mat-icon>
                  <img *ngIf="isWordFile(file); else pdfFileIcon" class="file-icon file-icon-img" src="assets/img/document_folders/doc.svg" alt="DOC">
                  <ng-template #pdfFileIcon><mat-icon class="file-icon">{{ isImageFile(file) ? 'image' : 'picture_as_pdf' }}</mat-icon></ng-template>
                  <span class="file-name" [title]="file.name">{{ file.name }}</span>
                  <mat-icon class="source-icon" [title]="file.isPickedDocument ? 'From document manager' : 'From local files'">{{ file.isPickedDocument ? 'folder_open' : 'upload_file' }}</mat-icon>
                  <button type="button" class="file-remove" (click)="removeAdditionalFile(file)" aria-label="Remove file">
                    <mat-icon>close</mat-icon>
                  </button>
                </li>
              }
            </ul>
          }
          <div
            class="pdf-upload-dropzone"
            [class.dragging]="isDraggingFile"
            (dragover)="onDropzoneDragOver($event)"
            (dragleave)="onDropzoneDragLeave($event)"
            (drop)="onDropzoneDrop($event)">
            <input #pdfFileInput type="file" multiple hidden [attr.accept]="fileInputAccept" (change)="onFileInputChange($event)" />
            <mat-icon class="upload-icon">cloud_upload</mat-icon>
            <p class="pdf-upload-text">Drag &amp; Drop a File Here</p>
            <p class="pdf-upload-or">or</p>
            <button type="button" class="btn-browse" (click)="pdfFileInput.click()">
              <mat-icon>upload</mat-icon>
              Browse local files
            </button>
          </div>

          <button type="button" class="btn-browse-modules" (click)="openBrowseAllModulesFilePicker()">
            <mat-icon>folder_open</mat-icon>
            Browse document manager
          </button>
          }

        }

        <!-- Validation Error -->
        <app-validation-error
          [formGroup]="sectionForm"
          [controlName]="'templates'"
          class="validation-error-enhanced">
        </app-validation-error>

        <!-- Empty State -->
        @if (!isLoadingTemplates && templates.length === 0) {
          <div class="template-empty-state">
            <mat-icon class="empty-icon">folder_open</mat-icon>
            <p class="empty-message">No templates available</p>
            <p class="empty-description">Please create templates in the settings to use this feature</p>
          </div>
        }

        <!-- Help Text -->
        @if (templates.length > 0) {
          <div class="template-help-text" id="template-help-text">
            <mat-icon class="help-icon">info</mat-icon>
            <span>Select one or more templates to generate your PDF. Default templates are pre-selected.</span>
          </div>
        }

        <!-- Error Message -->
        @if (exportError) {
          <div class="template-error-message">
            <mat-icon class="error-icon">error</mat-icon>
            <span>{{ exportError }}</span>
          </div>
        }
      </div>
    </div>

    <!-- Action Buttons Section -->
    <div class="action-buttons-section">
      <div class="action-buttons-grid">
        <!-- Send Email Button -->
        <div class="action-button-wrapper">
          <button type="button"
            class="action-btn action-btn-primary"
            [class.loading]="buttonStates.email === 'loading'"
            [disabled]="sectionForm.invalid || isProcessing"
            (click)="sendEmail()"
            aria-label="Send PDF via email">
            <span class="btn-content">
              <mat-icon>email</mat-icon>
              Send Email
            </span>
          </button>
        </div>

        <!-- Save Documents Button -->
        <div class="action-button-wrapper">
          <button type="button"
            class="action-btn action-btn-orange"
            (click)="openSaveDocumentPicker()"
            aria-label="Save PDF to documents">
            <span class="btn-content">
              <mat-icon>save</mat-icon>
              Save to Documents
            </span>
          </button>
        </div>

        <!-- Download Button -->
        <div class="action-button-wrapper">
          <button type="button"
            class="action-btn action-btn-primary"
            [class.loading]="buttonStates.download === 'loading'"
            [disabled]="sectionForm.invalid || isProcessing"
            (click)="downloadPdf()"
            aria-label="Download PDF file">
            <span class="btn-content">
              <mat-icon>download</mat-icon>
              Download PDF
            </span>
          </button>
        </div>

        <!-- Preview Button -->
        <div class="action-button-wrapper">
          <button type="button"
            class="action-btn action-btn-green"
            [class.loading]="buttonStates.preview === 'loading'"
            [disabled]="sectionForm.invalid || isProcessing"
            (click)="previewPdf()"
            aria-label="Preview PDF">
            <span class="btn-content">
              <mat-icon>visibility</mat-icon>
              Preview
            </span>
          </button>
        </div>
      </div>

      <!-- Success Message -->
      @if (exportSuccess) {
        <div class="success-message" [@fadeInOut]>
          <mat-icon class="success-icon">check_circle</mat-icon>
          <span>PDF exported successfully!</span>
        </div>
      }
    </div>
  </div>

  <!-- Accessibility Announcements -->
  <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
    @if (isLoadingTemplates) {
      <span>Loading templates</span>
    }
    @if (exportSuccess) {
      <span>PDF export completed successfully</span>
    }
    @if (exportError) {
      <span>Error: {{ exportError }}</span>
    }
  </div>
</div>